import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ParagraphTag from "../../../reusebale/Paragraph";
import ButtonTag from "../../../reusebale/button";
import LabelTag from "../../../reusebale/label";
import MyLoader from "../../../reusebale/myLoader";
import CardDisplay from "../../../reusebale/cardDisplay";
import AddCardModal from "../../../popup/addCardModal";
import ExternalAccountDisplay from "../../../reusebale/externalAccountDisplay";
import TransactionHistory from "../../view-profile/transaction-history";

import * as AuthAction from "../../../../store/actions/auth/AuthAction";
import { ShowError, ShowSuccess } from "../../../../utils/ToasFunction";

import BaseUrl from "../../../../constants/BaseUrl";
import db from "../../../../db/index.json";

const BankForm = ({ onBack }) => {
  const { completeProfileData } = db;
  const navigate = useNavigate();
  const { loginSuccess, userCompleteProfile } = useSelector(
    (state) => state.auth
  );
  const { allCitiesAndStates } = useSelector((state) => state.data);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState("");

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [contactMappingStatus, setContactMappingStatus] = useState(null);
  const [checkingContact, setCheckingContact] = useState(true);
  const [enablingPayments, setEnablingPayments] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [externalAccounts, setExternalAccounts] = useState([]);
  const [loadingBankInformation, setLoadingBankInformation] = useState(false);
  const [externalAccountLoading, setExternalAccountLoading] = useState(false);
  const [showExternalAccountForm, setShowExternalAccountForm] = useState(false);

  const [formFields, setFormFields] = useState({
    holderName: userCompleteProfile?.bankInformation
      ? userCompleteProfile?.bankInformation?.holderName
      : "",
    holderPhone: userCompleteProfile?.bankInformation
      ? userCompleteProfile?.bankInformation?.holderPhone
      : "",
    holderAddress1: userCompleteProfile?.bankInformation
      ? userCompleteProfile?.bankInformation?.holderAddress1
      : "",
    holderCity: userCompleteProfile?.bankInformation
      ? userCompleteProfile?.bankInformation?.holderCity
      : "",
    holderState: userCompleteProfile?.bankInformation
      ? userCompleteProfile?.bankInformation?.holderState
      : "",
    holderZip: userCompleteProfile?.bankInformation
      ? userCompleteProfile?.bankInformation?.holderZip
      : "",
    // External account fields
    accountNumber: "",
    holderType: "CONSUMER",
    accountType: "CHECKING",
    routingNumber: "",
    wireRoutingNumber: "",
  });

  // Fetch contact mapping status
  useEffect(() => {
    const fetchContactMappingStatus = async () => {
      try {
        setCheckingContact(true);
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        };

        const response = await fetch(
          `${BaseUrl}tenant/profile/getContactMappingStatus/${loginSuccess?.user?._id}`,
          requestOptions
        );
        const result = await response.json();

        if (result.status === "success") {
          setContactMappingStatus(result);
          console.log("✅ Contact mapping:", result.exists ? "Found" : "Not found");
        } else {
          console.error("❌ Failed to fetch contact mapping:", result.message);
        }
      } catch (error) {
        console.error("Error fetching contact mapping status:", error);
      } finally {
        setCheckingContact(false);
      }
    };

    if (loginSuccess?.user?._id) {
      fetchContactMappingStatus();
    }
  }, [loginSuccess]);

  // Fetch bank information (cards and external accounts) when contact mapping exists
  useEffect(() => {
    const fetchBankInformation = async () => {
      if (contactMappingStatus?.exists && loginSuccess?.user?._id) {
        try {
          setLoadingBankInformation(true);
          const myHeaders = new Headers();
          myHeaders.append("Content-Type", "application/json");
          myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

          const requestOptions = {
            method: "GET",
            headers: myHeaders,
            redirect: "follow",
          };

          const response = await fetch(
            `${BaseUrl}tenant/profile/getBankInformation/${loginSuccess.user._id}`,
            requestOptions
          );
          const result = await response.json();

          if (result.status === "success") {
            setCards(result.cards || []);
            setExternalAccounts(result.externalAccounts || []);
            console.log("✅ Bank information loaded:", "Cards:", result.cards?.length || 0, "| External Accounts:", result.externalAccounts?.length || 0);
          } else {
            setCards([]);
            setExternalAccounts([]);
            console.log("ℹ️ No bank information found");
          }
        } catch (error) {
          console.error("❌ Error fetching bank information:", error.message);
          setCards([]);
          setExternalAccounts([]);
        } finally {
          setLoadingBankInformation(false);
        }
      }
    };

    fetchBankInformation();
  }, [contactMappingStatus, loginSuccess]);

  // Update form fields when userCompleteProfile changes
  useEffect(() => {
    if (userCompleteProfile?.bankInformation) {
      setFormFields((prev) => ({
        ...prev,
        holderName: userCompleteProfile.bankInformation.holderName || "",
        holderPhone: userCompleteProfile.bankInformation.holderPhone || "",
        holderAddress1:
          userCompleteProfile.bankInformation.holderAddress1 || "",
        holderCity: userCompleteProfile.bankInformation.holderCity || "",
        holderState: userCompleteProfile.bankInformation.holderState || "",
        holderZip: userCompleteProfile.bankInformation.holderZip || "",
      }));
    }
  }, [userCompleteProfile]);

  // Pre-fill holder name when external account form is opened
  useEffect(() => {
    if (showExternalAccountForm) {
      // Get tenant name from personalInformation or loginSuccess
      const firstName = userCompleteProfile?.personalInformation?.firstName || loginSuccess?.user?.firstName || "";
      const lastName = userCompleteProfile?.personalInformation?.lastName || loginSuccess?.user?.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim();
      
      // Pre-fill holder name with tenant's name if available
      if (fullName) {
        setFormFields((prev) => ({
          ...prev,
          holderName: fullName,
        }));
      }
    }
  }, [showExternalAccountForm, userCompleteProfile, loginSuccess]);

  const UpdateBankProfile = () => {
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);
    const raw = JSON.stringify({
      tenantId: loginSuccess?.user?._id,
      makeAccount: true,
      bankInformation: {
        holderName: formFields.holderName,
        holderPhone: formFields.holderPhone,
        holderAddress1: formFields.holderAddress1,
        holderCity: formFields.holderCity,
        holderState: formFields.holderState,
        holderZip: formFields.holderZip,
      },
    });

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(`${BaseUrl}tenant/profile/createNewProfile`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "Success") {
          setLoading(false);
          ShowSuccess("Holder Information updated successfully");
          dispatch(
            AuthAction.GetTenantProfile,
            (loginSuccess?.user?._id, loginSuccess.token)
          );
          // onBack();
          setTimeout(() => {
            dispatch(AuthAction.LogoutAction());
            navigate("/login", { replace: true });
          }, 1000);
        } else {
          setLoading(false);
          ShowError(result.message);
        }
      })
      .catch((error) => console.error(error));
  };

  const UpdateHandler = (e) => {
    e.preventDefault();

    if (userCompleteProfile?.approvedStatus === "sendForApproval") {
      ShowError("Your profile request is pending for approval");
    } else {
      if (formFields.holderName === "") {
        ShowError("Holder name is required");
      } else if (formFields.holderPhone === "") {
        ShowError("Holder phone is required");
      } else if (formFields.holderAddress1 === "") {
        ShowError("Holder address is required");
      } else if (formFields.holderCity === "") {
        ShowError("Holder city is required");
      } else if (formFields.holderState === "") {
        ShowError("Holder state is required");
      } else if (formFields.holderZip === "") {
        ShowError("Holder zip code is required");
      } else {
        UpdateBankProfile();
      }
    }
  };

  const handleEnablePayments = async () => {
    // Check if personal information exists
    if (!userCompleteProfile?.personalInformation) {
      ShowError("Personal information is required to enable payments");
      return;
    }

    const { firstName, lastName, email } = userCompleteProfile.personalInformation;

    if (!firstName || !lastName || !email) {
      ShowError("Please complete your personal information first");
      return;
    }

    setEnablingPayments(true);

    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

      const raw = JSON.stringify({
        tenantId: loginSuccess?.user?._id,
        personalInformation: userCompleteProfile.personalInformation,
        companyInformation: userCompleteProfile.companyInformation,
        bankInformation: userCompleteProfile.bankInformation,
      });

      const requestOptions = {
        method: "PUT",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        `${BaseUrl}tenant/profile/createNewProfile`,
        requestOptions
      );
      const result = await response.json();

      if (result.status === "Success") {
        ShowSuccess("Payments process enabled successfully!");
        console.log("✅ Payments enabled successfully");
        
        // Refresh contact mapping status
        try {
          const statusResponse = await fetch(
            `${BaseUrl}tenant/profile/getContactMappingStatus/${loginSuccess?.user?._id}`,
            {
              method: "GET",
              headers: myHeaders,
              redirect: "follow",
            }
          );
          const statusResult = await statusResponse.json();
          
          if (statusResult.status === "success") {
            setContactMappingStatus(statusResult);
          }
        } catch (statusError) {
          console.error("❌ Error refreshing contact status:", statusError.message);
        }
      } else {
        console.error("❌ Enable payments failed:", result.message);
        ShowError(result.message || "Failed to enable payments. Please try again later.");
      }
    } catch (error) {
      console.error("Error enabling payments:", error);
      ShowError("There was an error enabling payments. Please try again later.");
    } finally {
      setEnablingPayments(false);
    }
  };

  const handleAddCard = async (cardData) => {
    if (!contactMappingStatus?.exists) {
      ShowError("Contact information not available. Please enable payments first.");
      return;
    }

    try {
      setCardLoading(true);
      
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

      const raw = JSON.stringify({
        cardData: cardData,
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        `${BaseUrl}tenant/profile/addCard/${loginSuccess.user._id}`,
        requestOptions
      );
      const result = await response.json();

      if (result.status === "success") {
        ShowSuccess("Card added successfully!");
        setShowAddCardModal(false);
        
        // Refresh bank information immediately to show the new card
        setLoadingBankInformation(true);
        try {
          const bankInfoResponse = await fetch(
            `${BaseUrl}tenant/profile/getBankInformation/${loginSuccess.user._id}`,
            {
              method: "GET",
              headers: myHeaders,
              redirect: "follow",
            }
          );
          const bankInfoResult = await bankInfoResponse.json();

          if (bankInfoResult.status === "success") {
            setCards(bankInfoResult.cards || []);
            setExternalAccounts(bankInfoResult.externalAccounts || []);
          }
        } catch (refreshError) {
          console.error("❌ Error refreshing bank information:", refreshError);
        } finally {
          setLoadingBankInformation(false);
        }
      } else {
        ShowError(result.message || "Failed to add card. Please try again.");
      }
    } catch (error) {
      console.error("Error adding card:", error);
      ShowError("There was an error adding the card. Please try again.");
    } finally {
      setCardLoading(false);
    }
  };


  const handleAddExternalAccount = async () => {
    if (!contactMappingStatus?.exists) {
      ShowError("Contact information not available. Please enable payments first.");
      return;
    }

    // Validate required fields
    if (!formFields.accountNumber) {
      ShowError("Account number is required");
      return;
    }
    if (!formFields.routingNumber) {
      ShowError("Routing number is required");
      return;
    }
    if (!formFields.wireRoutingNumber) {
      ShowError("Wire routing number is required");
      return;
    }
    if (!formFields.holderName) {
      ShowError("Holder name is required");
      return;
    }

    try {
      setExternalAccountLoading(true);
      
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

      const raw = JSON.stringify({
        externalAccountData: {
          accountNumber: formFields.accountNumber,
          holderType: formFields.holderType,
          type: formFields.accountType,
          routingNumber: formFields.routingNumber,
          wireRoutingNumber: formFields.wireRoutingNumber,
          holderName: formFields.holderName,
        },
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        `${BaseUrl}tenant/profile/addExternalAccount/${loginSuccess.user._id}`,
        requestOptions
      );
      const result = await response.json();

      if (result.status === "success") {
        ShowSuccess("External account added successfully!");
        setShowExternalAccountForm(false);
        
        // Reset form fields
        setFormFields((prev) => ({
          ...prev,
          accountNumber: "",
          routingNumber: "",
          wireRoutingNumber: "",
          holderName: "",
        }));
        
        // Refresh bank information immediately to show the new account
        setLoadingBankInformation(true);
        try {
          const bankInfoResponse = await fetch(
            `${BaseUrl}tenant/profile/getBankInformation/${loginSuccess.user._id}`,
            {
              method: "GET",
              headers: myHeaders,
              redirect: "follow",
            }
          );
          const bankInfoResult = await bankInfoResponse.json();

          if (bankInfoResult.status === "success") {
            setCards(bankInfoResult.cards || []);
            setExternalAccounts(bankInfoResult.externalAccounts || []);
          }
        } catch (refreshError) {
          console.error("❌ Error refreshing bank information:", refreshError);
        } finally {
          setLoadingBankInformation(false);
        }
      } else {
        ShowError(result.message || "Failed to add external account. Please try again.");
      }
    } catch (error) {
      console.error("Error adding external account:", error);
      ShowError("There was an error adding the external account. Please try again.");
    } finally {
      setExternalAccountLoading(false);
    }
  };


  return (
    <div className="bg-white rounded-xl">
      <div className="w-[95%] mx-auto py-5">
        <ParagraphTag
          content={completeProfileData.bankInformation.title}
          classes="text-darkColor font-popinsSemiBold text-lg mt-[-3px]"
        />

        {/* Payment Contact Status */}
        {!checkingContact && (
          <div className="mt-4">
            {contactMappingStatus?.exists ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-green-600 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <ParagraphTag
                    content="Payments process is enabled for your account."
                    classes="text-green-800 font-popinsSemiBold text-sm"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-yellow-600 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <ParagraphTag
                      content="Payments process not enabled."
                      classes="text-yellow-800 font-popinsSemiBold text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    className="bg-themeColor text-white px-4 py-2 rounded-md text-sm font-popinsSemiBold hover:bg-themeColor/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleEnablePayments}
                    disabled={enablingPayments}
                  >
                    {enablingPayments ? (
                      <div className="flex items-center">
                        <MyLoader />
                        <span className="ml-2">Enabling...</span>
                      </div>
                    ) : (
                      "Enable Payments"
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Bank Information Display Section */}
            {contactMappingStatus?.exists && (
              <>
                {loadingBankInformation ? (
                  <div className="mt-4 flex justify-center items-center py-4">
                    <MyLoader />
                    <span className="ml-2 text-gray-600 font-popinsMedium text-sm">
                      Loading bank information...
                    </span>
                  </div>
                ) : (
                  <>
                    <CardDisplay
                      cards={cards}
                      onAddCard={() => setShowAddCardModal(true)}
                    />
                    <ExternalAccountDisplay
                      externalAccounts={externalAccounts}
                      onAddAccount={() => setShowExternalAccountForm(true)}
                    />
                    {/* Transaction History Section */}
                    <TransactionHistory shouldRefresh={false} />
                  </>
                )}
              </>
            )}
          </div>
        )}

        <form>
          {/* Holder Information Fields - Commented Out */}
          {/* <div className="flex justify-between mt-5">
            <div className="w-[49%]">
              <LabelTag
                name={completeProfileData.bankInformation.label1}
                className="text-black"
              />
              <input
                value={formFields.holderName}
                onChange={(e) => {
                  setFormFields({
                    ...formFields,
                    holderName: e.target.value,
                  });
                }}
                type="text"
                placeholder="Enter holder name.."
                className="w-full mt-1 text-sm font-popinsRegular bg-bgColor px-3 py-2 rounded-lg text-darkColor placeholder:text-zinc-700/50"
              />
            </div>

            <div className="w-[49%]">
              <LabelTag
                name={completeProfileData.bankInformation.label2}
                className="text-black"
              />
              <input
                value={formFields.holderPhone}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only digits, spaces, hyphens, and parentheses for phone
                  if (/^[\d\s\-()]{0,15}$/.test(value)) {
                    setFormFields({
                      ...formFields,
                      holderPhone: value,
                    });
                  }
                }}
                type="text"
                placeholder="Enter holder phone number.."
                className="w-full mt-1 text-sm font-popinsRegular bg-bgColor px-3 py-2 rounded-lg text-darkColor placeholder:text-zinc-700/50"
              />
            </div>
          </div>
          <div className="flex justify-between">
            <div className="w-[49%] mt-3">
              <LabelTag
                name={completeProfileData.bankInformation.label3}
                className="text-black"
              />
              <input
                value={formFields.holderAddress1}
                onChange={(e) => {
                  setFormFields({
                    ...formFields,
                    holderAddress1: e.target.value,
                  });
                }}
                type="text"
                placeholder="Enter holder address.."
                className="w-full mt-1 text-sm font-popinsRegular bg-bgColor px-3 py-2 rounded-lg text-darkColor placeholder:text-zinc-700/50"
              />
            </div>
            <div className="w-[49%] mt-3">
              <LabelTag
                name={completeProfileData.bankInformation.label5}
                className="text-black"
              />
              <select
                value={formFields.holderState}
                onChange={(e) => {
                  setFormFields({
                    ...formFields,
                    holderState: e.target.value,
                  });
                  setSelectedState(e.target.value);
                }}
                className="w-full mt-1 text-sm font-popinsRegular bg-bgColor px-3 py-2 rounded-lg text-darkColor"
              >
                <option value="">-- Select a State --</option>
                {allCitiesAndStates.map((item, index) => (
                  <option key={index} value={item.state}>
                    {item.state}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-between">
            <div className="w-[49%] mt-3">
              <LabelTag
                name={completeProfileData.companyProfile.label6}
                className="text-black"
              />
              <select
                value={formFields.holderCity}
                onChange={(e) => {
                  setFormFields({
                    ...formFields,
                    holderCity: e.target.value,
                  });
                }}
                className="w-full mt-1 text-sm font-popinsRegular bg-bgColor px-3 py-2 rounded-lg text-darkColor"
                disabled={!selectedState || cities.length === 0} // Disable if no state selected
              >
                <option value="" disabled>
                  -- Select a City --
                </option>
                {cities.map((city) => (
                  <option key={city.id} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-[49%] mt-3">
              <LabelTag
                name={completeProfileData.bankInformation.label6}
                className="text-black"
              />
              <input
                value={formFields.holderZip}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only digits and restrict length to exactly 5
                  if (/^\d{0,5}$/.test(value)) {
                    setFormFields({
                      ...formFields,
                      holderZip: value,
                    });
                  }
                }}
                type="text"
                placeholder="Enter holder zip code.."
                className="w-full mt-1 text-sm font-popinsRegular bg-bgColor px-3 py-2 rounded-lg text-darkColor placeholder:text-zinc-700/50"
              />
            </div>
          </div> */}

          {/* External Account Form Section */}
          {showExternalAccountForm && contactMappingStatus?.exists && (
            <div className="mt-5 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <ParagraphTag
                  content="Add External Bank Account"
                  classes="text-darkColor font-popinsSemiBold text-base"
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowExternalAccountForm(false);
                    setFormFields((prev) => ({
                      ...prev,
                      accountNumber: "",
                      routingNumber: "",
                      wireRoutingNumber: "",
                      holderName: "",
                    }));
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Row 1: Holder Name, Holder Type, Account Type */}
                <div className="flex gap-4">
                  <div className="w-[32%]">
                    <LabelTag name="Holder Name*" className="text-black" />
                    <input
                      value={formFields.holderName}
                      onChange={(e) => {
                        setFormFields({
                          ...formFields,
                          holderName: e.target.value,
                        });
                      }}
                      type="text"
                      placeholder="Enter holder name.."
                      className="w-full mt-1 text-sm font-popinsRegular bg-white px-3 py-2 rounded-lg text-darkColor placeholder:text-zinc-700/50 border border-gray-300"
                    />
                  </div>

                  <div className="w-[32%]">
                    <LabelTag name="Holder Type*" className="text-black" />
                    <select
                      value={formFields.holderType}
                      onChange={(e) => {
                        setFormFields({
                          ...formFields,
                          holderType: e.target.value,
                        });
                      }}
                      className="w-full mt-1 text-sm font-popinsRegular bg-white px-3 py-2 rounded-lg text-darkColor border border-gray-300"
                    >
                      <option value="CONSUMER">Consumer</option>
                      <option value="CORPORATE">Corporate</option>
                    </select>
                  </div>

                  <div className="w-[32%]">
                    <LabelTag name="Account Type*" className="text-black" />
                    <select
                      value={formFields.accountType}
                      onChange={(e) => {
                        setFormFields({
                          ...formFields,
                          accountType: e.target.value,
                        });
                      }}
                      className="w-full mt-1 text-sm font-popinsRegular bg-white px-3 py-2 rounded-lg text-darkColor border border-gray-300"
                    >
                      <option value="CHECKING">Checking</option>
                      <option value="SAVINGS">Savings</option>
                    </select>
                  </div>
                </div>

                {/* Row 2: Account Number, Routing Number, Wire Routing Number */}
                <div className="flex gap-4">
                  <div className="w-[32%]">
                    <LabelTag name="Account Number*" className="text-black" />
                    <input
                      value={formFields.accountNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ""); // Only digits
                        setFormFields({
                          ...formFields,
                          accountNumber: value,
                        });
                      }}
                      type="text"
                      placeholder="Enter account number.."
                      className="w-full mt-1 text-sm font-popinsRegular bg-white px-3 py-2 rounded-lg text-darkColor placeholder:text-zinc-700/50 border border-gray-300"
                    />
                  </div>

                  <div className="w-[32%]">
                    <LabelTag name="Routing Number*" className="text-black" />
                    <input
                      value={formFields.routingNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ""); // Only digits
                        if (value.length <= 9) {
                          setFormFields({
                            ...formFields,
                            routingNumber: value,
                          });
                        }
                      }}
                      type="text"
                      placeholder="Enter routing number.."
                      className="w-full mt-1 text-sm font-popinsRegular bg-white px-3 py-2 rounded-lg text-darkColor placeholder:text-zinc-700/50 border border-gray-300"
                    />
                  </div>

                  <div className="w-[32%]">
                    <LabelTag name="Wire Routing Number*" className="text-black" />
                    <input
                      value={formFields.wireRoutingNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ""); // Only digits
                        if (value.length <= 9) {
                          setFormFields({
                            ...formFields,
                            wireRoutingNumber: value,
                          });
                        }
                      }}
                      type="text"
                      placeholder="Enter wire routing number.."
                      className="w-full mt-1 text-sm font-popinsRegular bg-white px-3 py-2 rounded-lg text-darkColor placeholder:text-zinc-700/50 border border-gray-300"
                    />
                  </div>
                </div>

                <div className="w-full bg-themeColor text-white rounded-md">
                  {externalAccountLoading ? (
                    <div className="flex w-full justify-center items-center py-2">
                      <MyLoader />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAddExternalAccount}
                      className="w-full py-2 text-sm font-popinsSemiBold"
                    >
                      Add External Account
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Save Button - Removed */}
          {/* <div className="w-full bg-themeColor text-white mt-5 rounded-md">
            {loading ? (
              <div className="flex w-full justify-center items-center py-2">
                <MyLoader />
              </div>
            ) : (
              <>
                {!userCompleteProfile?.bankInformation?.BankAccountID &&
                  userCompleteProfile?.personalInformation &&
                  userCompleteProfile?.companyInformation && (
                    <ButtonTag onSubmit={UpdateHandler} name="Save" />
                  )}
              </>
            )}
          </div> */}
        </form>
      </div>

      {/* Add Card Modal */}
      {showAddCardModal && (
        <AddCardModal
          onClose={() => setShowAddCardModal(false)}
          onCardAdded={handleAddCard}
          isLoading={cardLoading}
        />
      )}
    </div>
  );
};

export default BankForm;
